import React, { useEffect, useState } from "react";
import {
  Spin,
  Button,
  Row,
  Input,
  Space,
  Card,
  Typography,
  Modal,
  DatePicker,
  Col,
  Select,
} from "antd";
import {
  ArrowRight,
  CircleAlert,
  ExternalLink,
  Medal,
  Search,
} from "lucide-react";
import { FONT } from "constants/index";
import { notificationErroInternal } from "util/api-utils";
import { ColumnsUserAqua } from "./columns-table";
import DrawerApp from "components/drawer-app";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment";
import {
  getPhasesAquatic,
  getReviewStats,
  getTraining,
  saveAnnotationAquatic,
  updateTask,
} from "services/aquatic/aquatic-service";
import FormNewUserReviewMultipleAqua from "./reviews-drawer-new-review-multiple-aqua";
import { getUsers } from "services/user/user-service";
import DetailAquatic from "../managerial/managerial-detail-aqua";
import ReviewsStats from "./reviews-stats";
import useBreakpoint from "hooks/useBreakpoint";
import { listMinWidth } from "util/responsive";
import ResponsiveDataList from "components/responsive-data-list";

dayjs.extend(customParseFormat);
const dateFormat = "DD/MM/YYYY";
const { RangePicker } = DatePicker;

const { Text } = Typography;
const { confirm } = Modal;

const levels = [
  { label: "AVENTUREIROS", value: "Aventureiros" },
  { label: "CONQUISTADORES", value: "Conquistadores" },
  { label: "BEBÊ ÁGUA", value: "Natação Bebê" },
  { label: "DESCOBRIDORES", value: "Descobridores" },
  { label: "EXPLORADORES", value: "Exploradores" },
  { label: "NAVEGADORES", value: "Navegadores" },
];

const ReviewsClientAquaKids = () => {
  const { isMobile, width } = useBreakpoint();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(null);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [openDrawers, setOpenDrawers] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [phases, setPhases] = useState([{ subphases: [] }]);
  const [userTrainer, setUserTrainer] = useState(null);
  const [editId, setEditId] = useState(null);
  const [level, setLevel] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [stats, setStats] = useState({ users: 0, reviews: 0 });

  useEffect(() => {
    refreshData();
    getPhases();
  }, []);

  function refreshData() {
    setLoading(true);
    getData();
  }

  const getPhases = async () => {
    try {
      const data = await getPhasesAquatic();
      setPhases(data);
    } catch (error) {
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde.",
      );
    }
  };
  async function getData() {
    try {
      let start = null;
      let end = null;
      if (dateRange && dateRange[0] && dateRange[1]) {
        start = dateRange[0].format("YYYY-MM-DDTHH:mm:ss-03:00");
        end = dateRange[1].format("YYYY-MM-DDTHH:mm:ss-03:00");
      }

      let obj = {
        search: search,
        level: level,
        start: start,
        end: end,
      };

      obj = Object.fromEntries(
        Object.entries(obj).filter(
          ([key, value]) => value !== undefined && value != "" && value != null,
        ),
      );

      const [reviews, reviewsStats] = await Promise.all([
        getUsers(obj),
        getReviewStats("KIDS"),
      ]);

      const filteredUsers = reviews
        .filter((item) => item.isBoot === false)
        .map((obj) => ({
          ...obj,
          edit: edit,
        }));

      setData(filteredUsers);
      setStats(reviewsStats);
    } catch (err) {
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde.",
      );
    } finally {
      setLoading(false);
    }
  }
  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setEditId(null);
    setOpen(false);
    refreshData();
  };
  const onCloseDrawers = () => {
    setOpenDrawers(false);
    refreshData();
    setSelectedRowKeys([]);
    setIsEdit(false);
  };

  async function edit(row) {
    setEditId(row.id);
    setLoading(true);
    try {
      const data = await getTraining(row.id);
      setUserTrainer(data);
      showDrawer();
    } catch (error) {
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde.",
      );
    } finally {
      setLoading(false);
    }
  }

  const onSelectChange = (newSelectedRowKeys) => {
    const newSelectedKeys = [...selectedRowKeys];

    newSelectedRowKeys.forEach((key) => {
      if (!newSelectedKeys.includes(key)) {
        newSelectedKeys.push(key);
      }
    });

    const allKeys = newSelectedKeys.filter((key) =>
      newSelectedRowKeys.includes(key),
    );
    if (allKeys.length <= 6) {
      setSelectedRowKeys(allKeys);
    }
  };

  const findSubphaseById = (subphaseId) => {
    for (const phase of phases) {
      const found = phase.subphases.find((sub) => sub.id === subphaseId);
      if (found) {
        return { phase, subphase: found };
      }
    }
    return null;
  };
  const calcPercent = (tasksDone = [], objectives = []) => {
    const objectiveTaskIds = new Set(
      objectives.flatMap((obj) => obj.tasks?.map((task) => task.id) || []),
    );

    const filteredDone = tasksDone.filter((done) =>
      objectiveTaskIds.has(done.taskId),
    );

    const totalTasks = objectiveTaskIds.size;
    const doneCount = filteredDone.length;

    if (totalTasks === 0) return 0;

    const percent = (doneCount / totalTasks) * 100;

    return Math.round(percent);
  };

  const saveTask = async (status, taskId, userId) => {
    setLoading(true);

    try {
      await updateTask(taskId + "/" + status, { userId: userId });
      const nowDate = moment(new Date());

      const obj = {
        trainingId: userTrainer.training.id,
        annotation: "Avalição Avulsa - " + nowDate.format("DD/MM/YYYY"),
        isBoot: false,
      };
      await saveAnnotationAquatic(obj);
      const data = await getTraining(userId);
      setUserTrainer(data);
    } catch (error) {
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde.",
      );
    } finally {
      setLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    columnWidth: "10%",
    preserveSelectedRowKeys: true,
  };
  const hasSelected = selectedRowKeys.length > 0;

  const mountFilter = () => {
    return (
      <Row
        className="reviews-filter-row"
        gutter={[16, 16]}
        style={{ paddingBottom: "10px", alignItems: "center" }}
      >
        <Col xs={24} md={6} lg={5}>
          <Input
            allowClear
            value={search ?? ""}
            placeholder="Pesquisar Aluno"
            onChange={(e) => setSearch(e.target.value)}
            prefix={<Search size={18} color="#e0e0e0" />}
            style={{ width: "100%" }}
            onPressEnter={refreshData}
          />
        </Col>

        <Col xs={24} md={6} lg={5}>
          <Select
            showSearch
            mode="multiple"
            allowClear
            maxTagCount={1}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            style={{ width: "100%" }}
            options={levels}
            onChange={setLevel}
            placeholder="Selecionar Nível..."
          />
        </Col>
        <Col xs={24} md={8} lg={8}>
          <RangePicker
            showTime={{ format: "HH:mm" }}
            format="DD/MM/YYYY HH:mm"
            placeholder={["Início", "Fim"]}
            style={{ width: "100%" }}
            onChange={(dates, dateStrings) => {
              setDateRange(dates);
            }}
          />
        </Col>

        <Col xs={24} md={4} lg={4}>
          <Button
            style={{ fontFamily: FONT, width: "100%" }}
            type="primary"
            icon={
              <ArrowRight
                size={18}
                style={{ marginBottom: "-3px", marginRight: "7px" }}
              />
            }
            onClick={refreshData}
          >
            Filtrar
          </Button>
        </Col>
        {hasSelected && (
          <Col xs={24}>
            <Space wrap>
              <Text style={{ fontFamily: FONT }}>
                {selectedRowKeys.length} alunos selecionados
              </Text>
              <Button
                style={{ fontFamily: FONT }}
                type="primary"
                icon={
                  <ExternalLink
                    size={18}
                    style={{ marginBottom: "-3px", marginRight: "7px" }}
                  />
                }
                onClick={() => setOpenDrawers(true)}
              >
                Abrir Todos
              </Button>
              <Button
                style={{
                  fontFamily: FONT,
                  backgroundColor: "#09ac9c",
                }}
                type="primary"
                icon={
                  <ExternalLink
                    size={18}
                    style={{ marginBottom: "-3px", marginRight: "7px" }}
                  />
                }
                onClick={() => {
                  setIsEdit(true);
                  setOpenDrawers(true);
                }}
              >
                Editar Todos
              </Button>
            </Space>
          </Col>
        )}
      </Row>
    );
  };

  return (
    <div className="container-reviews-client">
      <ReviewsStats stats={stats} isLoading={loading} />

      <Spin size="large" spinning={loading}>
        {mountFilter()}
        <Row gutter={[24, 24]} style={{ marginLeft: "0px" }}>
          <Space
            direction="vertical"
            size={"small"}
            style={{
              flex: "1 1 500px",
              minWidth: listMinWidth(isMobile),
            }}
          >
            <Card
              id="card-table"
              className="card-table-list"
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <Medal
                      size={18}
                      style={{ marginBottom: "-4px", marginRight: "10px" }}
                    />
                    <Text style={{ fontSize: "15px", fontFamily: FONT }}>
                      Avaliações dos Alunos
                    </Text>
                  </div>
                </div>
              }
              bordered={false}
            >
              <ResponsiveDataList
                isMobile={isMobile}
                forceCardLayout
                cardVariant="profile"
                rowKey={"id"}
                columns={ColumnsUserAqua(true)}
                dataSource={data}
                //rowSelection={rowSelection}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                locale={{
                  emptyText: "Sem registro",
                }}
              />
            </Card>
          </Space>
        </Row>
      </Spin>

      <DrawerApp
        open={open}
        onClose={onClose}
        title={"Nova Avaliação"}
        body={
          <DetailAquatic
            user={userTrainer}
            findSubphaseById={findSubphaseById}
            calcPercent={calcPercent}
            allPhases={phases}
            saveTask={saveTask}
          />
        }
        size={"large"}
        isOnlyView={true}
      />

      {openDrawers && (
        <DrawerApp
          open={openDrawers}
          onClose={onCloseDrawers}
          title={isEdit ? "Editar Avaliação" : "Nova Avaliação"}
          body={
            <FormNewUserReviewMultipleAqua
              data={data.filter((item) => selectedRowKeys.includes(item.id))}
              onClose={onCloseDrawers}
              isEdit={isEdit}
            />
          }
          isOnlyView={true}
          size={"large"}
        />
      )}
    </div>
  );
};
export default ReviewsClientAquaKids;
